import React, { useCallback, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm, Resolver } from 'react-hook-form';
import * as Yup from 'yup';

import { sendEmail } from '../../../../api';
import { ControlledCheckboxField } from '../../../../components/FormControlsV2/ControlledCheckboxField';
import { ControlledFileField } from '../../../../components/FormControlsV2/ControlledFileField';
import { ControlledTextField } from '../../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../../components/FormControlsV2/FormProvider';
import { ERRORS_TEXTS } from '../../../../const/validation';

type FormFields = {
  subject: string;
  document: string;
  documentAgreement: string;
  email: string;
  isChecked: boolean;
};

const schema = Yup.object().shape({
  subject: Yup.string().required(ERRORS_TEXTS.required),
  document: Yup.string().required(ERRORS_TEXTS.required),
  documentAgreement: Yup.string().required(ERRORS_TEXTS.required),
  email: Yup.string(),
  isChecked: Yup.boolean(),
});

const defaultValues: FormFields = {
  subject: '',
  document: '',
  documentAgreement: '',
  email: '',
  isChecked: false,
};

export const Agreement: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormFields>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema) as unknown as Resolver<FormFields>,
  });

  const handleFormSubmit = useCallback(
    async (values: FormFields) => {
      setLoading(true);
      setError('');

      const payload = {
        templateFileName: values.document,
        subject: values.subject,
        isChecked: values.isChecked,
        ...(values.email && { testEmail: values.email }),
        agreementNumbersFileName: values.documentAgreement,
        skip: 0,
      };

      let allEmailsSent = false;

      while (true) {
        try {
          const response = await sendEmail(payload);

          if (response.success) {
            const { total = 0, sent = 0 } = response.result;

            if (sent >= total) {
              allEmailsSent = true;
              break;
            }

            payload.skip = sent;
          } else {
            setError(response.errorMessage ?? '');
            break;
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
          break;
        } finally {
          setLoading(false);
        }
      }

      if (allEmailsSent) {
        enqueueSnackbar('Письма отправлены');
      }
    },
    [enqueueSnackbar],
  );

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} autoComplete="off" noValidate>
      <FormProvider control={control} errors={errors}>
        <ControlledTextField name="subject" label="Тема письма" />
        <ControlledTextField name="email" label="Тестовый email" />

        <ControlledFileField
          name="document"
          placeholder="Шаблон письма"
          url="/api/admin/files/upload_tmp"
          fileTypes={['.htm']}
          sizeView="kb"
          onRemove={() => setValue('document', '', { shouldDirty: true })}
          onUpload={(_fieldName, fileName) => setValue('document', fileName, { shouldDirty: true })}
        />

        <ControlledFileField
          name="documentAgreement"
          placeholder="Список ген.соглашений"
          url="/api/admin/files/upload_tmp"
          fileTypes={['.txt']}
          sizeView="kb"
          onRemove={() => setValue('documentAgreement', '', { shouldDirty: true })}
          onUpload={(_fieldName, fileName) => setValue('documentAgreement', fileName, { shouldDirty: true })}
        />

        <ControlledCheckboxField name="isChecked" label="Шаблон проверен" />
      </FormProvider>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isDirty || loading}
          fullWidth
          startIcon={loading && <CircularProgress size={20} />}
        >
          Отправить
        </Button>
      </Box>
    </form>
  );
};
